import * as XLSX from "xlsx";

export const tariffStorageKey = "zentraqTariffs";
export const activeTariffStorageKey = "zentraqActiveTariffId";

export const emptyTariff = {
  id: "",
  carrierName: "Kuehne+Nagel",
  tariffName: "",
  tariffType: "Ausgang",
  country: "",
  relation: "",
  validFrom: "",
  validTo: "",
  currency: "EUR",
  weightBreaks: [],
  zones: [],
  prices: {},
  postalCodeZones: [],
  packageRules: {
    euroPalletKg: 175,
    oneWayPalletKg: 175,
    cagePalletKg: 250,
    halfPalletKg: 90,
    quarterPalletKg: 60,
    cbmKg: 150,
    loadingMeterKg: 1250,
  },
  surcharges: [],
  insuranceTable: [],
  toll: [],
  internationalToll: [],
  dieselTable: [],
  zoneCount: 0,
  createdAt: "",
  updatedAt: "",
  isActive: false,
};

const defaultWeightBreaks = [
  100, 150, 200, 250, 300, 400, 500, 600, 700, 800, 900, 1000, 1100, 1200,
  1300, 1400, 1500, 1600, 1700, 1800, 1900, 2000, 2500,
];

const defaultZones = [1, 2, 3, 4, 5, 6, 7, 8];

export function createEmptyTariffDraft() {
  return {
    ...emptyTariff,
    id: `tariff-${Date.now()}`,
    tariffName: "Neuer Tarif",
    country: "",
    relation: "",
    zoneCount: 0,
    weightBreaks: [...defaultWeightBreaks],
    zones: [...defaultZones],
    prices: {},
    postalCodeZones: [],
    packageRules: { ...emptyTariff.packageRules },
    surcharges: [],
    insuranceTable: [],
    toll: [],
    internationalToll: [],
    dieselTable: [],
  };
}

export function parseLocalizedNumber(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  let normalized = String(value)
    .trim()
    .replace(/\s/g, "")
    .replace(/[^0-9,.-]/g, "");

  if (!normalized) {
    return null;
  }

  const lastComma = normalized.lastIndexOf(",");
  const lastDot = normalized.lastIndexOf(".");

  if (lastComma >= 0 && lastDot >= 0) {
    normalized = lastComma > lastDot
      ? normalized.replace(/\./g, "").replace(",", ".")
      : normalized.replace(/,/g, "");
  } else if (lastComma >= 0) {
    normalized = normalized.replace(",", ".");
  }

  const parsed = Number(normalized);

  return Number.isFinite(parsed) ? parsed : null;
}

export function parseList(value) {
  if (Array.isArray(value)) {
    return value.map(parseLocalizedNumber).filter((item) => item !== null);
  }

  return String(value || "")
    .split(/[\n;,]+/)
    .map(parseLocalizedNumber)
    .filter((item) => item !== null);
}

function parseCsvLine(line, delimiter) {
  const cells = [];
  let current = "";
  let quoted = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    const nextChar = line[index + 1];

    if (char === '"' && nextChar === '"') {
      current += '"';
      index += 1;
      continue;
    }

    if (char === '"') {
      quoted = !quoted;
      continue;
    }

    if (char === delimiter && !quoted) {
      cells.push(current.trim());
      current = "";
      continue;
    }

    current += char;
  }

  cells.push(current.trim());
  return cells;
}

export function parseCsv(text) {
  const lines = String(text || "")
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .filter((line) => line.trim());
  const firstLine = lines[0] || "";
  const delimiter = firstLine.includes(";") ? ";" : ",";
  const rows = lines.map((line) => parseCsvLine(line, delimiter));
  const headers = rows[0] || [];
  const dataRows = rows.slice(1);

  return { headers, rows: dataRows, delimiter };
}

function normalizeKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function findHeaderIndex(headers, candidates) {
  const normalizedHeaders = headers.map(normalizeKey);
  return candidates
    .map(normalizeKey)
    .map((candidate) => normalizedHeaders.indexOf(candidate))
    .find((index) => index >= 0);
}

export function inferTariffFromCsv(text) {
  const parsed = parseCsv(text);
  const headers = parsed.headers;
  const weightIndex = findHeaderIndex(headers, ["gewicht", "weight", "gewichtsstufe", "weightBreak"]);
  const fromPrefixIndex = findHeaderIndex(headers, ["fromPrefix", "plzVon", "von", "prefixVon"]);
  const toPrefixIndex = findHeaderIndex(headers, ["toPrefix", "plzBis", "bis", "prefixBis"]);
  const zoneIndex = findHeaderIndex(headers, ["zone", "tarifzone"]);
  const zoneColumns = headers
    .map((header, index) => {
      const match = String(header).match(/(?:zone\s*)?([1-9][0-9]?)/i);
      return match ? { zone: Number(match[1]), index } : null;
    })
    .filter(Boolean);
  const draft = createEmptyTariffDraft();
  const previewRows = parsed.rows.map((row) =>
    Object.fromEntries(headers.map((header, index) => [header || `Spalte ${index + 1}`, row[index] || ""]))
  );

  if (weightIndex >= 0 && zoneColumns.length > 0) {
    const weights = [];
    const zones = Array.from(new Set(zoneColumns.map((column) => column.zone))).sort((a, b) => a - b);
    const prices = {};

    parsed.rows.forEach((row) => {
      const weight = parseLocalizedNumber(row[weightIndex]);

      if (weight === null) {
        return;
      }

      weights.push(weight);
      prices[String(weight)] = {};
      zoneColumns.forEach((column) => {
        const price = parseLocalizedNumber(row[column.index]);

        if (price !== null) {
          prices[String(weight)][String(column.zone)] = price;
        }
      });
    });

    draft.weightBreaks = Array.from(new Set(weights)).sort((a, b) => a - b);
    draft.zones = zones;
    draft.prices = prices;
  }

  if (fromPrefixIndex >= 0 && toPrefixIndex >= 0 && zoneIndex >= 0) {
    draft.postalCodeZones = parsed.rows
      .map((row) => ({
        fromPrefix: String(row[fromPrefixIndex] || "").trim(),
        toPrefix: String(row[toPrefixIndex] || "").trim(),
        zone: parseLocalizedNumber(row[zoneIndex]),
      }))
      .filter((entry) => entry.fromPrefix && entry.toPrefix && entry.zone !== null);
  }

  return {
    draft,
    preview: {
      type: "csv",
      tables: [{ name: "CSV Tabelle", headers, rows: previewRows }],
      messages: ["CSV wurde gelesen. Erkannte Spalten koennen im Mapping korrigiert werden."],
    },
  };
}

function inferTariffFromRows(headers, rows, sourceName) {
  const csvText = [
    headers,
    ...rows.map((row) => headers.map((header) => row[header] ?? "")),
  ]
    .map((row) =>
      row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(";")
    )
    .join("\n");
  const inferred = inferTariffFromCsv(csvText);

  return {
    draft: inferred.draft,
    preview: {
      type: "xlsx",
      tables: [{ name: sourceName, headers, rows }],
      messages: ["XLSX wurde gelesen. Erkannte Spalten koennen im Mapping korrigiert werden."],
    },
  };
}
function normalizeDate(value) {
  const text = String(value || "").trim();
  const match = text.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);

  if (match) {
    return `${match[3]}-${match[2].padStart(2, "0")}-${match[1].padStart(2, "0")}`;
  }

  return text;
}

function getSheetRows(workbook, sheetName) {
  const worksheet = workbook.Sheets[sheetName];
  return worksheet ? XLSX.utils.sheet_to_json(worksheet, { defval: "", raw: false }) : [];
}

function buildPreviewTables(workbook) {
  return workbook.SheetNames.map((sheetName) => {
    const rows = getSheetRows(workbook, sheetName);
    return {
      name: sheetName,
      headers: rows.length ? Object.keys(rows[0]) : [],
      rows,
    };
  });
}

function metaValue(metaRows, fieldName) {
  const wanted = normalizeKey(fieldName);
  const match = metaRows.find((row) => {
    const field = normalizeKey(row.Feld || row.Field || row.Key || row.Name);
    return field === wanted || field.includes(wanted);
  });

  if (match) {
    return String(match.Wert ?? match.Value ?? match.value ?? "").trim();
  }

  for (const row of metaRows) {
    const key = Object.keys(row).find((column) => normalizeKey(column) === wanted);

    if (key && row[key] !== "") {
      return String(row[key]).trim();
    }
  }

  return "";
}

function countryDefaults(country, relation) {
  const raw = String(`${country || ""} ${relation || ""}`).trim().toUpperCase();

  if (raw === "NL" || raw.includes("NIEDER") || raw.includes("NETHER")) {
    return { country: "NL", relation: "Niederlande", zoneCount: 4 };
  }

  if (raw === "DE" || raw.includes("DEUTSCH") || raw.includes("GERMAN")) {
    return { country: "DE", relation: "Deutschland", zoneCount: 8 };
  }

  return { country: String(country || "").trim(), relation: String(relation || country || "").trim(), zoneCount: 0 };
}

function buildMeta(metaRows) {
  const country = metaValue(metaRows, "country") || metaValue(metaRows, "Land");
  const relation = metaValue(metaRows, "relation") || metaValue(metaRows, "Relation") || metaValue(metaRows, "Land/Relation") || country;
  const defaults = countryDefaults(country, relation);
  const zoneCount = parseLocalizedNumber(metaValue(metaRows, "zoneCount")) || defaults.zoneCount;

  return {
    carrierName: metaValue(metaRows, "carrierName") || metaValue(metaRows, "Spedition") || "Kuehne+Nagel",
    tariffName: metaValue(metaRows, "tariffName") || "Kuehne+Nagel 2026",
    country: defaults.country,
    relation: defaults.relation,
    tariffType: metaValue(metaRows, "tariffType") || metaValue(metaRows, "Tarifart") || "Ausgang",
    validFrom: normalizeDate(metaValue(metaRows, "validFrom") || metaValue(metaRows, "von")),
    validTo: normalizeDate(metaValue(metaRows, "validTo") || metaValue(metaRows, "bis")),
    zoneCount,
    currency: metaValue(metaRows, "currency") || metaValue(metaRows, "Waehrung") || metaValue(metaRows, "Währung") || "EUR",
    raw: metaRows.reduce((meta, row) => {
      const field = String(row.Feld || row.Field || row.Key || row.Name || "").trim();

      if (field) {
        return { ...meta, [field]: String(row.Wert ?? row.Value ?? row.value ?? "").trim() };
      }

      return {
        ...meta,
        ...Object.fromEntries(Object.entries(row).map(([key, value]) => [String(key).trim(), String(value).trim()])),
      };
    }, {}),
  };
}

function parsePriceSheet(rows, id, label, tariffType, zoneCount = null, overrides = {}) {
  const firstRow = rows[0] || {};
  const weightKey = Object.keys(firstRow).find((key) => normalizeKey(key) === "gewichtkg" || normalizeKey(key) === "gewicht" || normalizeKey(key) === "weightkg") || "Gewicht_kg";
  const zoneColumns = Object.keys(firstRow)
    .filter((key) => key !== weightKey)
    .map((key) => {
      const zoneMatch = key.match(/^Zone(\d+)$/i);
      const distanceMatch = key.match(/^bis_(\d+)_km$/i);
      const zone = zoneMatch ? Number(zoneMatch[1]) : distanceMatch ? `bis_${distanceMatch[1]}_km` : key;

      if (!zoneMatch && !distanceMatch) {
        return null;
      }

      if (zoneMatch && zoneCount && zone > zoneCount) {
        return null;
      }

      return {
        key,
        zone,
        label: zoneMatch ? `Zone ${zoneMatch[1]}` : key.replace(/_/g, " "),
      };
    })
    .filter(Boolean);
  const weightBreaks = [];
  const prices = {};

  rows.forEach((row) => {
    const weight = parseLocalizedNumber(row[weightKey]);

    if (weight === null) {
      return;
    }

    weightBreaks.push(weight);
    prices[String(weight)] = {};
    zoneColumns.forEach((column) => {
      const price = parseLocalizedNumber(row[column.key]);

      if (price !== null) {
        prices[String(weight)][String(column.zone)] = price;
      }
    });
  });

  return {
    id,
    label,
    tariffType,
    ...overrides,
    weightBreaks: Array.from(new Set(weightBreaks)).sort((a, b) => a - b),
    zones: zoneColumns.map((column) => column.zone),
    zoneLabels: Object.fromEntries(zoneColumns.map((column) => [String(column.zone), column.label])),
    prices,
  };
}

function parsePackageRules(rows) {
  const rules = { ...emptyTariff.packageRules };

  rows.forEach((row) => {
    const code = String(row.Code || "").trim();
    const value = parseLocalizedNumber(row.Mindestgewicht_kg);

    if (value === null) {
      return;
    }

    if (code === "FP/EP") {
      rules.euroPalletKg = value;
      rules.oneWayPalletKg = value;
    } else if (code === "GP") {
      rules.cagePalletKg = value;
    } else if (code === "HP") {
      rules.halfPalletKg = value;
    } else if (code === "VP") {
      rules.quarterPalletKg = value;
    } else if (code === "CBM") {
      rules.cbmKg = value;
    } else if (code === "LDM") {
      rules.loadingMeterKg = value;
    }
  });

  return rules;
}

function parseInsuranceTable(rows) {
  return rows
    .map((row) => {
      const goodsValue = parseLocalizedNumber(
        row.goodsValue ?? row.Warenwert ?? row.Warenwert_EUR ?? row.Warenwert_bis_EUR ?? row.Bis_Warenwert_EUR
      );
      const premium = parseLocalizedNumber(
        row.premium ?? row.Praemie ?? row.Praemie_EUR ?? row.Versicherung_EUR
      );

      return { goodsValue, premium };
    })
    .filter((row) => row.goodsValue !== null && row.premium !== null)
    .sort((left, right) => left.goodsValue - right.goodsValue);
}
function firstExistingSheet(workbook, candidates) {
  return candidates.find((sheetName) => workbook.SheetNames.includes(sheetName)) || "";
}

function buildKuehneNagelWorkbookTariff(workbook) {
  const metaRows = getSheetRows(workbook, "Tarif_Meta");
  const meta = buildMeta(metaRows);
  const tableSpecs = [
    {
      sheet: firstExistingSheet(workbook, ["Ausgang", "KN_Ausgang_DE"]),
      id: "outbound-de",
      label: "Ausgang Deutschland",
      tariffType: "Ausgang",
      country: "DE",
      relation: "Deutschland",
      zoneCount: 8,
    },
    {
      sheet: firstExistingSheet(workbook, ["NL_Export", "KN_NL_Export"]),
      id: "export-nl",
      label: "Export Niederlande",
      tariffType: "Export",
      country: "NL",
      relation: "Niederlande",
      zoneCount: 4,
    },
    {
      sheet: firstExistingSheet(workbook, ["KN_Eingang_DE", "Eingang", "KN_NL_Import"]),
      id: "inbound",
      label: "Eingang",
      tariffType: "Eingang",
      country: meta.country,
      relation: meta.relation,
      zoneCount: meta.zoneCount,
    },
    {
      sheet: firstExistingSheet(workbook, ["KN_PickupQuer_DE", "Pickup", "PickupQuer"]),
      id: "pickup-cross",
      label: "Pickup Quer",
      tariffType: "Pickup",
      country: meta.country,
      relation: meta.relation,
      zoneCount: meta.zoneCount,
    },
  ].filter((spec) => spec.sheet);
  const tariffTables = tableSpecs
    .map((spec) => parsePriceSheet(getSheetRows(workbook, spec.sheet), spec.id, spec.label, spec.tariffType, spec.zoneCount, {
      sheetName: spec.sheet,
      country: spec.country,
      relation: spec.relation,
      zoneCount: spec.zoneCount,
    }))
    .filter((table) => table.weightBreaks.length > 0);
  const defaultTable = tariffTables[0] || createEmptyTariffDraft();
  const postalCodeZones = getSheetRows(workbook, "PLZ_Zonen")
    .map((row) => ({
      country: String(row.Land || defaultTable.country || meta.country || "").trim(),
      fromPrefix: String(row.PLZ_von || "").trim().padStart(2, "0"),
      toPrefix: String(row.PLZ_bis || "").trim().padStart(2, "0"),
      zone: parseLocalizedNumber(row.Zone),
    }))
    .filter((entry) => entry.fromPrefix && entry.toPrefix && entry.zone !== null);
  const packageRules = parsePackageRules(getSheetRows(workbook, "Packmittel_Regeln"));
  const surchargeSheet = firstExistingSheet(workbook, ["Zuschlaege_National", "Zuschlaege"]);
  const tollSheet = firstExistingSheet(workbook, ["Maut_DE", "Maut_International"]);
  const insuranceSheet = firstExistingSheet(workbook, ["Transportversicherung", "Versicherung", "Versicherung_National", "Insurance"]);
  const surcharges = getSheetRows(workbook, surchargeSheet).map((row, index) => ({
    id: `${String(row.Code || "Z").trim()}-${index}`,
    code: String(row.Code || "").trim(),
    label: String(row.Bezeichnung || "").trim(),
    calculationType: String(row.Berechnungsart || "").trim(),
    value: parseLocalizedNumber(row.Wert),
    condition: String(row.Nebenbedingung || "").trim(),
    minimumAmount: parseLocalizedNumber(row.Minimum_EUR),
    scope: String(row.Geltungsbereich || "").trim(),
  }));
  const toll = getSheetRows(workbook, tollSheet).map((row) => ({ ...row }));
  const internationalToll = getSheetRows(workbook, firstExistingSheet(workbook, ["Maut_International", "Maut_International_NL"])).map((row) => ({ ...row }));
  const dieselTable = getSheetRows(workbook, firstExistingSheet(workbook, ["Diesel_Stueckgut", "Diesel"])).map((row) => ({ ...row }));
  const insuranceTable = parseInsuranceTable(getSheetRows(workbook, insuranceSheet));
  return normalizeTariff({
    id: `kn-2026-${Date.now()}`,
    carrierName: meta.carrierName,
    tariffName: meta.tariffName,
    tariffType: defaultTable.tariffType || meta.tariffType,
    country: defaultTable.country || meta.country,
    relation: defaultTable.relation || meta.relation,
    validFrom: meta.validFrom,
    validTo: meta.validTo,
    currency: meta.currency,
    weightBreaks: defaultTable.weightBreaks,
    zones: defaultTable.zones,
    prices: defaultTable.prices,
    postalCodeZones,
    packageRules,
    surcharges,
    insuranceTable,
    toll,
    internationalToll,
    dieselTable,
    tariffTables,
    zoneCount: defaultTable.zoneCount || meta.zoneCount,
    meta: meta.raw,
  });
}

function isKuehneNagelWorkbook(workbook) {
  const hasMeta = workbook.SheetNames.includes("Tarif_Meta");
  const hasPriceSheet = [
    "KN_Ausgang_DE",
    "KN_Eingang_DE",
    "Ausgang",
    "Eingang",
    "NL_Export",
    "KN_NL_Export",
    "KN_NL_Import",
  ].some((sheetName) => workbook.SheetNames.includes(sheetName));

  return hasMeta && hasPriceSheet;
}

function buildImportSummaryRows(tariff, workbook) {
  const tables = Array.isArray(tariff.tariffTables) && tariff.tariffTables.length > 0
    ? tariff.tariffTables
    : [{
        sheetName: "",
        label: tariff.tariffName,
        tariffType: tariff.tariffType,
        country: tariff.country,
        relation: tariff.relation,
        zoneCount: tariff.zoneCount,
        weightBreaks: tariff.weightBreaks,
        zones: tariff.zones,
        prices: tariff.prices,
      }];

  return tables.map((table) => ({
    Blatt: table.sheetName || table.label || "",
    Tarif: table.label || table.tariffType || "",
    Land: table.country || tariff.country || "",
    Relation: table.relation || tariff.relation || "",
    Tarifart: table.tariffType || tariff.tariffType || "",
    Gueltig_von: tariff.validFrom || "",
    Gueltig_bis: tariff.validTo || "",
    Preiszeilen: table.weightBreaks?.length || 0,
    Zonen: table.zones?.length || 0,
    zoneCount: table.zoneCount || tariff.zoneCount || 0,
    Erkannte_Blaetter: workbook.SheetNames.join(", "),
  }));
}
export function normalizeTariff(value) {
  const now = new Date().toISOString();
  const source = value || {};

  return {
    ...emptyTariff,
    ...source,
    id: source.id || `tariff-${Date.now()}`,
    carrierName: String(source.carrierName || "Kuehne+Nagel").trim(),
    tariffName: String(source.tariffName || "Importierter Tarif").trim(),
    tariffType: String(source.tariffType || "Ausgang").trim(),
    currency: String(source.currency || "EUR").trim(),
    weightBreaks: parseList(source.weightBreaks).sort((a, b) => a - b),
    zones: parseList(source.zones).sort((a, b) => a - b),
    prices: source.prices || {},
    postalCodeZones: Array.isArray(source.postalCodeZones) ? source.postalCodeZones : [],
    packageRules: { ...emptyTariff.packageRules, ...(source.packageRules || {}) },
    surcharges: Array.isArray(source.surcharges) ? source.surcharges : [],
    insuranceTable: Array.isArray(source.insuranceTable) ? source.insuranceTable.map((row) => ({
      goodsValue: parseLocalizedNumber(row.goodsValue ?? row.Warenwert ?? row.Warenwert_EUR),
      premium: parseLocalizedNumber(row.premium ?? row.Praemie ?? row.Praemie_EUR),
    })).filter((row) => row.goodsValue !== null && row.premium !== null).sort((left, right) => left.goodsValue - right.goodsValue) : [],
    tariffTables: Array.isArray(source.tariffTables) ? source.tariffTables : [],
    toll: Array.isArray(source.toll) ? source.toll : [],
    meta: source.meta || {},
    createdAt: source.createdAt || now,
    updatedAt: source.updatedAt || now,
    tenantId: source.tenantId || "default",
    customerId: source.customerId || "default",
    relation: String(source.relation || source.meta?.relation || source.meta?.Relation || source.country || source.meta?.country || "").trim(),
    country: String(source.country || source.meta?.country || source.meta?.Land || "").trim(),
    status: source.status || (source.isActive ? "active" : "draft"),
    originalFileName: source.originalFileName || "",
    importedAt: source.importedAt || source.createdAt || now,
    importedBy: source.importedBy || "",
    importDate: source.importDate || "",
    note: source.note || "",
    importLog: source.importLog || null,
    parentImportId: source.parentImportId || "",
    zoneCount: parseLocalizedNumber(source.zoneCount ?? source.meta?.zoneCount) || 0,
    isActive: source.status ? source.status === "active" : Boolean(source.isActive),
  };
}

export async function analyzeTariffFile(file) {
  const fileName = file?.name || "";
  const fileType = file?.type || "";
  const extension = fileName.split(".").pop()?.toLowerCase();

  if (extension === "json" || fileType === "application/json") {
    const text = await file.text();
    const parsed = JSON.parse(text);
    const tariff = normalizeTariff(parsed.tariff || parsed);

    return {
      draft: tariff,
      preview: {
        type: "json",
        tables: [{ name: "JSON Tarifmodell", headers: ["Feld", "Wert"], rows: Object.entries(tariff).map(([key, value]) => ({ Feld: key, Wert: JSON.stringify(value) })) }],
        messages: ["JSON wurde als Tarifmodell gelesen."],
      },
    };
  }

  if (extension === "csv" || fileType === "text/csv") {
    return inferTariffFromCsv(await file.text());
  }

  if (extension === "xlsx") {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: "array" });
    const previewTables = buildPreviewTables(workbook);

    if (isKuehneNagelWorkbook(workbook)) {
      const draft = buildKuehneNagelWorkbookTariff(workbook);
      const summaryRows = buildImportSummaryRows(draft, workbook);

      return {
        draft,
        preview: {
          type: "xlsx",
          tables: [
            {
              name: "Import-Zusammenfassung",
              headers: ["Blatt", "Tarif", "Land", "Relation", "Tarifart", "Gueltig_von", "Gueltig_bis", "Preiszeilen", "Zonen", "zoneCount", "Erkannte_Blaetter"],
              rows: summaryRows,
            },
            ...previewTables,
          ],
          messages: [
            "Kuehne+Nagel-Importstruktur erkannt.",
            "Tarif_Meta, Preisblaetter, Zonen, Land/Relation und Gueltigkeit wurden strukturiert uebernommen.",
          ],
        },
      };
    }

    const firstSheetName = workbook.SheetNames[0];
    const rows = firstSheetName ? getSheetRows(workbook, firstSheetName) : [];
    const headers = rows.length ? Object.keys(rows[0]) : [];

    if (!headers.length) {
      return {
        draft: createEmptyTariffDraft(),
        preview: {
          type: "xlsx",
          tables: previewTables,
          messages: ["XLSX wurde gelesen, aber es wurden keine Tabellenzeilen erkannt."],
        },
      };
    }

    const inferred = inferTariffFromRows(headers, rows, firstSheetName || "XLSX Tabelle");
    return {
      draft: inferred.draft,
      preview: { ...inferred.preview, tables: previewTables },
    };
  }

  if (extension === "pdf" || fileType === "application/pdf") {
    const buffer = await file.arrayBuffer();
    const text = new TextDecoder("latin1").decode(buffer);
    const snippets = text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => /\d/.test(line))
      .slice(0, 40)
      .map((line, index) => ({ Zeile: index + 1, Text: line.slice(0, 240) }));

    return {
      draft: createEmptyTariffDraft(),
      preview: {
        type: "pdf",
        tables: [{ name: "PDF Textauszug", headers: ["Zeile", "Text"], rows: snippets }],
        messages: [
          "PDF wurde nur als Hilfsquelle ausgelesen.",
          "Daten werden nicht automatisch aktiviert. Bitte Werte pruefen oder CSV/JSON verwenden.",
        ],
      },
    };
  }

  throw new Error("Dieser Dateityp wird nicht unterstuetzt.");
}

export function validateTariff(tariff) {
  const errors = [];
  const warnings = [];

  if (!String(tariff.carrierName || "").trim()) {
    errors.push("Spedition muss gepflegt sein.");
  }

  if (!String(tariff.country || "").trim()) {
    errors.push("Land muss gepflegt sein.");
  }

  if (!String(tariff.relation || "").trim()) {
    errors.push("Relation muss gepflegt sein.");
  }

  if (!String(tariff.tariffType || "").trim()) {
    errors.push("Tarifart muss gepflegt sein.");
  }

  const parsedZoneCount = parseLocalizedNumber(tariff.zoneCount);

  if (parsedZoneCount === null || parsedZoneCount <= 0) {
    errors.push("Anzahl Zonen muss als zoneCount gepflegt sein.");
  }
  if (!tariff.validFrom || !tariff.validTo) {
    errors.push("Gueltig von und Gueltig bis muessen gepflegt sein.");
  }
  const tables = Array.isArray(tariff.tariffTables) && tariff.tariffTables.length > 0
    ? tariff.tariffTables
    : [{
        label: tariff.tariffType || tariff.tariffName || "Tarif",
        weightBreaks: tariff.weightBreaks || [],
        zones: tariff.zones || [],
        prices: tariff.prices || {},
      }];

  tables.forEach((table) => {
    const weightBreaks = table.weightBreaks || [];
    const zones = table.zones || [];
    const tableLabel = table.label || table.tariffType || "Tarif";

    if (weightBreaks.length === 0) {
      errors.push(`${tableLabel}: Es sind keine Gewichtsstufen vorhanden.`);
    }

    if (zones.length === 0) {
      errors.push(`${tableLabel}: Es sind keine Zonen vorhanden.`);
    }

    weightBreaks.forEach((weightBreak) => {
      const row = table.prices?.[String(weightBreak)] || {};
      zones.forEach((zone) => {
        const price = parseLocalizedNumber(row[String(zone)]);

        if (price === null) {
          errors.push(`${tableLabel}: Preis fehlt: ${weightBreak} kg / Zone ${zone}.`);
        } else if (price < 0) {
          errors.push(`${tableLabel}: Ungueltiger Preis: ${weightBreak} kg / Zone ${zone}.`);
        }
      });
    });
  });

  if (!Array.isArray(tariff.postalCodeZones) || tariff.postalCodeZones.length === 0) {
    warnings.push("Es sind keine PLZ-Zonen gepflegt.");
  } else {
    const seen = new Set();

    tariff.postalCodeZones.forEach((entry) => {
      const from = Number(entry.fromPrefix);
      const to = Number(entry.toPrefix);
      const key = `${entry.fromPrefix}-${entry.toPrefix}`;

      if (!entry.fromPrefix || !entry.toPrefix || !entry.zone) {
        errors.push("PLZ-Zonen enthalten unvollstaendige Eintraege.");
      }

      if (Number.isNaN(from) || Number.isNaN(to) || from > to) {
        errors.push(`Ungueltiger PLZ-Bereich: ${entry.fromPrefix}-${entry.toPrefix}.`);
      }

      if (seen.has(key)) {
        errors.push(`Doppelter PLZ-Bereich: ${key}.`);
      }

      seen.add(key);
    });
  }
    const sortedRanges = (Array.isArray(tariff.postalCodeZones) ? tariff.postalCodeZones : [])
      .map((entry) => ({ from: Number(entry.fromPrefix), to: Number(entry.toPrefix) }))
      .filter((entry) => !Number.isNaN(entry.from) && !Number.isNaN(entry.to))
      .sort((left, right) => left.from - right.from);

    sortedRanges.forEach((entry, index) => {
      const previous = sortedRanges[index - 1];

      if (previous && entry.from <= previous.to) {
        errors.push(`PLZ-Bereich ueberlappt: ${String(entry.from).padStart(2, "0")}-${String(entry.to).padStart(2, "0")}.`);
      }
    });

  (tariff.insuranceTable || []).forEach((row) => {
    if (parseLocalizedNumber(row.goodsValue) === null || parseLocalizedNumber(row.premium) === null || Number(row.goodsValue) < 0 || Number(row.premium) < 0) {
      errors.push("Ungueltiger Wert in der Versicherungstabelle.");
    }
  });

  Object.entries(tariff.packageRules || {}).forEach(([key, value]) => {
    const parsed = parseLocalizedNumber(value);

    if (parsed === null || parsed < 0) {
      errors.push(`Ungueltiger Wert in Sperrigkeits-/Mindestgewichten: ${key}.`);
    }
  });

  return {
    ok: errors.length === 0,
    errors,
    warnings,
  };
}

export function tariffToCsv(tariff) {
  const zones = tariff.zones || [];
  const header = ["Gewicht", ...zones.map((zone) => `Zone ${zone}`)];
  const rows = (tariff.weightBreaks || []).map((weightBreak) => {
    const row = tariff.prices?.[String(weightBreak)] || {};
    return [weightBreak, ...zones.map((zone) => row[String(zone)] ?? "")];
  });

  return [header, ...rows]
    .map((row) =>
      row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(";")
    )
    .join("\n");
}

export function downloadTextFile(fileName, content, type) {
  const blob = new Blob([content], { type });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
















export const tariffStatuses = {
  draft: "Entwurf",
  checked: "geprueft",
  active: "aktiv",
  archived: "archiviert",
};

export function getTariffs() {
  try {
    return JSON.parse(localStorage.getItem(tariffStorageKey) || "[]");
  } catch (error) {
    return [];
  }
}

export function saveTariffs(tariffs) {
  localStorage.setItem(tariffStorageKey, JSON.stringify(tariffs));
}

export function getRelationLabel(tariff) {
  const raw = String(tariff?.relation || tariff?.country || tariff?.meta?.relation || tariff?.meta?.country || "").trim().toUpperCase();

  if (raw === "NL" || raw.includes("NIEDER")) {
    return "Niederlande";
  }

  if (raw === "DE" || raw.includes("DEUTSCH")) {
    return "Deutschland";
  }

  return raw || "Unbekannt";
}

export function getTariffTypeLabel(value, relation) {
  const raw = String(value || "Ausgang").trim();

  if (relation === "Niederlande" && raw === "Ausgang") {
    return "Export";
  }

  if (relation === "Niederlande" && raw === "Eingang") {
    return "Import";
  }

  return raw;
}

export function getTariffCombinationKey(tariff) {
  return [tariff.carrierName, getRelationLabel(tariff), tariff.tariffType]
    .map((item) => String(item || "").trim().toLowerCase())
    .join("|");
}

function createTariffId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return `tariff-${crypto.randomUUID()}`;
  }

  return `tariff-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function createTariffRecordsFromDraft(draft, { fileName = "", user = null, preview = null, validation = null } = {}) {
  const now = new Date().toISOString();
  const relation = String(draft.relation || "").trim();
  const country = String(draft.country || "").trim();
  const tables = Array.isArray(draft.tariffTables) && draft.tariffTables.length > 0
    ? draft.tariffTables
    : [{
        id: draft.tariffType || "default",
        label: draft.tariffType || draft.tariffName,
        tariffType: draft.tariffType,
        weightBreaks: draft.weightBreaks || [],
        zones: draft.zones || [],
        prices: draft.prices || {},
      }];
  const importLogBase = {
    timestamp: now,
    fileName,
    importedBy: user?.username || "",
    sheets: preview?.tables?.map((table) => table.name) || [],
    postalZoneCount: draft.postalCodeZones?.length || 0,
    warnings: validation?.warnings || [],
    errors: validation?.errors || [],
    messages: preview?.messages || [],
  };

  return tables.map((table, index) => {
    const tableRelation = String(table.relation || relation).trim();
    const tableCountry = String(table.country || country).trim();
    const tableZoneCount = table.zoneCount || draft.zoneCount || table.zones?.length || 0;
    const tariffType = getTariffTypeLabel(table.tariffType || table.label || draft.tariffType, tableRelation);
    const priceCount = Object.values(table.prices || {}).reduce(
      (sum, row) => sum + Object.keys(row || {}).length,
      0
    );

    return normalizeTariff({
      ...draft,
      id: createTariffId(),
      parentImportId: draft.id || createTariffId(),
      tenantId: draft.tenantId || "default",
      customerId: draft.customerId || "default",
      relation: tableRelation,
      country: tableCountry,
      tariffName: tables.length > 1 ? `${draft.tariffName} - ${tariffType}` : draft.tariffName,
      tariffType,
      status: validation?.ok ? "checked" : "draft",
      isActive: false,
      originalFileName: fileName,
      importedAt: now,
      importedBy: user?.username || "",
      importDate: new Date().toLocaleString("de-DE"),
      note: draft.note || "",
      weightBreaks: table.weightBreaks || [],
      zones: table.zones || [],
      prices: table.prices || {},
      tariffTables: [table],
      importLog: {
        ...importLogBase,
        tariffType,
        priceCount,
        weightBreakCount: table.weightBreaks?.length || 0,
        zoneCount: tableZoneCount,
      },
      zoneCount: tableZoneCount,
      createdAt: now,
      updatedAt: now,
    });
  });
}

export function activateTariffById(tariffs, tariffId) {
  const selected = tariffs.find((tariff) => tariff.id === tariffId);

  if (!selected) {
    return tariffs;
  }

  const selectedKey = getTariffCombinationKey(selected);
  return tariffs.map((tariff) => {
    if (tariff.id === tariffId) {
      return { ...tariff, status: "active", isActive: true, updatedAt: new Date().toISOString() };
    }

    if (getTariffCombinationKey(tariff) === selectedKey && tariff.status === "active") {
      return { ...tariff, status: "archived", isActive: false, updatedAt: new Date().toISOString() };
    }

    return tariff;
  });
}




















